import { FC, useState } from 'react';
import { FieldError, Control, UseFormGetValues } from 'react-hook-form';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text,
} from '@chakra-ui/react';
import { NotificationFormFields } from 'page/NotificationPage/types/notificationFormField';
import { DateTimePicker } from 'components/atoms/DateTimePicker';
import { parseDateTimeString } from 'utils/dateUtils';

type Props = {
  control: Control<NotificationFormFields>;
  getValues: UseFormGetValues<NotificationFormFields>;
  startDateError: FieldError | undefined;
  endDateError: FieldError | undefined;
};

const getDate = (value: unknown) => {
  if (typeof value !== 'string') return null;

  return parseDateTimeString(value);
};

/**
 * 表示期間のフォームです。
 * Chakra の Text は非制御コンポーネントなので、react-hook-form の register でフォーム化しています。
 */
export const DateRangeForm: FC<Props> = ({
  control,
  getValues,
  startDateError,
  endDateError,
}) => {
  // 開始日時と終了日時のカレンダーが連動するが、RHF の状態変化では再レンダリングされないので
  // 変更を useState で受けてフォームの値に応じてカレンダーが変わるようにする。
  const [startDate, setStartDate] = useState<Date | null>(
    getDate(getValues('startDate')),
  );
  const [endDate, setEndDate] = useState<Date | null>(
    getDate(getValues('endDate')),
  );

  return (
    <Flex flexDirection="row" gap={5} alignItems="start">
      <Box w="330px">
        <FormControl isRequired isInvalid={Boolean(startDateError)}>
          <FormLabel>表示開始日時</FormLabel>
          <DateTimePicker
            controllerProps={{
              control,
              name: 'startDate',
              rules: {
                required: '必須項目です',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  setStartDate(getDate(e.target.value));
                },
              },
            }}
            datePickerProps={{
              maxDate: endDate,
            }}
            asString
          />
          {startDateError && startDateError.message && (
            <FormErrorMessage>{startDateError.message}</FormErrorMessage>
          )}
          {startDateError && startDateError.type === 'invalidDate' && (
            <FormErrorMessage>不正な日時です</FormErrorMessage>
          )}
        </FormControl>
      </Box>
      <Box w="20px" pt="40px">
        <Text as="span">〜</Text>
      </Box>
      <Box w="330px">
        <FormControl isRequired isInvalid={Boolean(endDateError)}>
          <FormLabel>表示終了日時</FormLabel>
          <DateTimePicker
            controllerProps={{
              control,
              name: 'endDate',
              rules: {
                required: '必須項目です',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  setEndDate(getDate(e.target.value));
                },
              },
            }}
            datePickerProps={{
              minDate: startDate,
            }}
            asString
          />
          {endDateError && endDateError.message && (
            <FormErrorMessage>{endDateError.message}</FormErrorMessage>
          )}
          {endDateError && endDateError.type === 'invalidDate' && (
            <FormErrorMessage>不正な日時です</FormErrorMessage>
          )}
        </FormControl>
      </Box>
    </Flex>
  );
};
